import "@/App.css";
import React, { useCallback } from "react";
import { createBrowserRouter, RouterProvider, Routes, Route, Navigate, useLocation, useBlocker } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { UnsavedChangesProvider, useUnsaved } from "@/context/UnsavedChanges";
import { Toaster } from "@/components/ui/sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Contacts from "@/pages/Contacts";
import Lieux from "@/pages/Lieux";
import ContactDetail from "@/pages/ContactDetail";
import CalendarPage from "@/pages/CalendarPage";
import Quotes from "@/pages/Quotes";
import QuoteEditor from "@/pages/QuoteEditor";
import SettingsModal from "@/components/SettingsModal";
import AccountModal from "@/components/AccountModal";
import Entreprise from "@/pages/Entreprise";
import Comptabilite from "@/pages/Comptabilite";
import Storefront from "@/pages/Storefront";
import Stock from "@/pages/Stock";
import Expenses from "@/pages/Expenses";
import LoadingList from "@/pages/LoadingList";
import ScreenDisplay from "@/pages/ScreenDisplay";
import Team from "@/pages/Team";
import Planning from "@/pages/Planning";
import ModulePage from "@/pages/ModulePage";
import EventDetail from "@/pages/EventDetail";
import Portal from "@/pages/Portal";
import Invitation from "@/pages/Invitation";
import CardInvite from "@/pages/CardInvite";
import Valhalla from "@/pages/Valhalla";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Deliveries from "@/pages/Deliveries";
import api from "@/lib/api";
import { MaintenancePage } from "@/components/MaintenancePage";

function Loader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#09090b]">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-zinc-700 border-t-[#FF6A00]" />
    </div>
  );
}

function Protected({ children }) {
  const { user, org, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (org && !org.onboarding_done && location.pathname !== "/onboarding")
    return <Navigate to="/onboarding" replace />;
  return children;
}

function ThemeApplier() {
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();
  const location = useLocation();
  React.useEffect(() => {
    const pt = user?.prefs?.theme;
    if (pt && pt !== theme) setTheme(pt);
    // eslint-disable-next-line
  }, [user?.prefs?.theme]);
  React.useEffect(() => {
    const el = document.documentElement;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const apply = () => {
      let light;
      if (location.pathname.startsWith("/portail")) light = true;
      else if (location.pathname.startsWith("/boutique")) light = true;
      else if (user?.roles?.includes("ecran")) light = false;
      else light = theme === "light" || (theme === "system" && mq.matches);
      el.classList.toggle("light", light);
    };
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [theme, location.pathname, user]);
  return null;
}

function MaintenanceGate({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [status, setStatus] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    const fetchStatus = () => api.get("/platform/status").then(({ data }) => { if (alive) setStatus(data); }).catch(() => {});
    fetchStatus();
    const t = setInterval(fetchStatus, 15000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  if (status?.maintenance && location.pathname !== "/superadmin" && location.pathname !== "/valhalla" && !user?.is_platform_admin) {
    return <MaintenancePage status={status} />;
  }
  return children;
}

function NavigationGuard() {
  const { dirty } = useUnsaved();
  const blocker = useBlocker(
    useCallback(({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname, [dirty])
  );
  const blocked = blocker.state === "blocked";
  return (
    <AlertDialog open={blocked} onOpenChange={(o) => { if (!o && blocked) blocker.reset(); }}>
      <AlertDialogContent className="border-border bg-card" data-testid="nav-guard-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#FFD500]/15 text-[#FF6A00]">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
            </span>
            Modifications non enregistrées
          </AlertDialogTitle>
          <AlertDialogDescription>
            Vous avez des modifications non enregistrées sur cette page. Si vous quittez maintenant, elles seront perdues.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="nav-guard-stay" onClick={() => blocker.reset()}>Rester sur la page</AlertDialogCancel>
          <AlertDialogAction data-testid="nav-guard-leave" onClick={() => blocker.proceed()} className="bg-red-600 text-white hover:bg-red-700">Quitter sans enregistrer</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const isScreen = user?.roles?.includes("ecran");
  const location = useLocation();
  const backgroundLocation = location.state?.backgroundLocation;
  if (!loading && isScreen) {
    return (
      <>
        <ThemeApplier />
        <MaintenanceGate>
          <Routes>
            <Route path="*" element={<Protected><ScreenDisplay /></Protected>} />
          </Routes>
        </MaintenanceGate>
      </>
    );
  }
  return (
    <>
    <ThemeApplier />
    <NavigationGuard />
    <MaintenanceGate>
    <Routes location={backgroundLocation || location}>
      <Route path="/login" element={loading ? <Loader /> : user ? <Navigate to="/app" replace /> : <Login />} />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/portail/:token" element={<Portal />} />
      <Route path="/invitation/:token" element={<Invitation />} />
      <Route path="/abonnement-carte/:token" element={<CardInvite />} />
      <Route path="/boutique/:slug" element={<Storefront />} />
      <Route path="/superadmin" element={<Valhalla />} />
      <Route path="/valhalla" element={<Valhalla />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/app" element={<Protected><Layout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="contacts" element={<Contacts />} />
        <Route path="contacts/:id" element={<ContactDetail />} />
        <Route path="lieux" element={<Lieux />} />
        <Route path="calendrier" element={<CalendarPage />} />
        <Route path="evenements/:id" element={<EventDetail />} />
        <Route path="devis" element={<Quotes />} />
        <Route path="devis/nouveau" element={<QuoteEditor />} />
        <Route path="devis/:id" element={<QuoteEditor />} />
        <Route path="comptabilite" element={<Comptabilite />} />
        <Route path="parametres" element={<SettingsModal />} />
        <Route path="compte" element={<AccountModal />} />
        <Route path="entreprise/:tab" element={<Entreprise />} />
        <Route path="stock" element={<Stock />} />
        <Route path="notes-de-frais" element={<Expenses />} />
        <Route path="chargement" element={<LoadingList />} />
        <Route path="livraisons" element={<Deliveries />} />
        <Route path="planning" element={<Planning />} />
        <Route path="equipe" element={<Team />} />
        <Route path="module/:key" element={<ModulePage />} />
      </Route>
      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
    {backgroundLocation && (
      <Routes>
        <Route path="/app/parametres" element={<Protected><SettingsModal /></Protected>} />
        <Route path="/app/compte" element={<Protected><AccountModal /></Protected>} />
      </Routes>
    )}
    </MaintenanceGate>
    </>
  );
}

const router = createBrowserRouter([{ path: "*", element: <AppRoutes /> }]);

export default function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <UnsavedChangesProvider>
            <RouterProvider router={router} />
            <Toaster position="bottom-right" richColors />
          </UnsavedChangesProvider>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}
